// AB#3281 — the semantic status-colour vocabulary, and its ONE resolution to the theme's colours.
//
// A module never says "green" and never says a hex: it says what a value MEANS (success, danger, …)
// and the theme decides what that looks like in light and in dark. The vocabulary is CLOSED — seven
// words — and is enforced at the import gate (MpackValidator.ValidateStatusTones), mirrored by the
// C# EnumOption.Tones list; this module is the third copy of the list and the only place the words
// become classes. A word added to one copy without the others either never installs or never renders.
//
// Colour is never the only signal: every consumer keeps its text (the pill label, the list cell
// value), so a toned UI still reads correctly in monochrome.

/** One of the seven semantic tone words a module may declare on an enum option or lookup value. */
export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'accent1' | 'accent2'

/** The closed vocabulary, in its canonical order. */
export const STATUS_TONES: readonly StatusTone[] =
    ['neutral', 'info', 'success', 'warning', 'danger', 'accent1', 'accent2']

export const toneChartColors: Record<StatusTone, { light: string; dark: string }> = {
    neutral: { light: '#737373', dark: '#a3a3a3' }, info: { light: '#3b82f6', dark: '#60a5fa' },
    success: { light: '#16a34a', dark: '#4ade80' }, warning: { light: '#d97706', dark: '#fbbf24' },
    danger: { light: '#dc2626', dark: '#f87171' }, accent1: { light: '#7c3aed', dark: '#a78bfa' },
    accent2: { light: '#0d9488', dark: '#2dd4bf' },
}

/**
 * Pill (badge) classes per tone, light + dark. The light halves match the pills the renderer already
 * paints (HeaderStatusPill's green/neutral, the blue default), so a module that declares `info` gets
 * exactly the pill an undeclared enum gets today — the vocabulary extends the existing look, it does
 * not introduce a second one.
 */
export const tonePillClasses: Record<StatusTone, string> = {
    neutral: 'bg-neutral-100 text-neutral-600 dark:bg-slate-700 dark:text-slate-300',
    info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    success: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    danger: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    accent1: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300',
    accent2: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
}

/**
 * Left-edge row-accent border classes per tone (list.rowAccent). Saturated rather than the pill's
 * tinted background: a 4px strip needs more chroma than a filled pill to register at all.
 */
export const toneAccentBorderClasses: Record<StatusTone, string> = {
    neutral: 'border-l-neutral-400 dark:border-l-slate-500',
    info: 'border-l-blue-500 dark:border-l-blue-400',
    success: 'border-l-green-500 dark:border-l-green-400',
    warning: 'border-l-amber-500 dark:border-l-amber-400',
    danger: 'border-l-red-500 dark:border-l-red-400',
    accent1: 'border-l-violet-500 dark:border-l-violet-400',
    accent2: 'border-l-teal-500 dark:border-l-teal-400',
}

/** Narrowing guard: is this string one of the seven vocabulary words? */
export function isStatusTone(value: unknown): value is StatusTone {
    return typeof value === 'string' && (STATUS_TONES as readonly string[]).includes(value)
}

/**
 * Resolves the declared tone for a stored value from a field's enum-option entries
 * (`ViewFieldMeta.enum` — bare codes or `{ value, label?, tone? }` objects).
 *
 * Returns undefined for a bare-string option, an untoned object, an unknown value, or a tone word
 * outside the vocabulary — the gate refuses those at import, but a definition predating the gate (or
 * hand-edited in the DB) must degrade to today's untoned rendering, never crash or half-colour.
 */
export function resolveOptionTone(
    entries: readonly (string | { value: string; label?: string | null; tone?: string | null })[] | null | undefined,
    value: unknown,
): StatusTone | undefined {
    if (!entries || value === null || value === undefined || value === '') { return undefined }
    const code = String(value)
    for (const e of entries) {
        if (typeof e === 'string' || e.value !== code) { continue }
        return isStatusTone(e.tone) ? e.tone : undefined
    }
    return undefined
}
